import React, { useState, useEffect, useRef } from 'react';
import { Bell, BookOpen, Brain, Users, ArrowRight } from 'lucide-react';

interface ServicesProps {
  onOpenRegister?: () => void;
}

const services = [
  {
    icon: Bell,
    title: 'Alertes Trading',
    description:
      'Recevez des alertes précises en temps réel pour saisir les meilleures opportunités du marché.',
    image: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&q=80',
    stats: [
      { value: '2 à 4', label: 'trades par jour' },
      { value: '1/4', label: 'ratio risque/reward minimum' },
      { value: '1%', label: 'de risque maximum par trade' },
    ],
    features: [
      'Plusieurs marchés: Gold, DJ30, Crudeoil, EURUSD, Bitcoin, etc...',
      '1% de risque maximum par trade',
      'Notifications en temps réel',
      'Suivi des performances',
    ],
    bgImage:
      'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&q=80&w=1920&h=1080',
    color: '#9333EA',
  },
  {
    icon: BookOpen,
    title: 'Formation Complète',
    description:
      'Accédez à une formation détaillée pour maîtriser les stratégies qui fonctionnent vraiment.',
    image: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&q=80',
    stats: [
      { value: '5', label: 'modules' },
      { value: '100+', label: 'exercices' },
      { value: 'Vidéos/PDF', label: 'explicatifs' },
    ],
    features: [
      'Cours vidéo',
      'Exercices pratiques',
      'Études de cas réels',
      'Certification finale',
    ],
    bgImage:
      'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?auto=format&fit=crop&q=80&w=1920&h=1080',
    color: '#A855F7',
  },
  {
    icon: Brain,
    title: 'Analyse IA',
    description:
      'Profitez de notre intelligence artificielle pour détecter les meilleures opportunités du marché.',
    image: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?auto=format&fit=crop&q=80',
    stats: [
      { value: 'IA', label: 'dernière gen' },
      { value: '+5', label: 'analyses fondamentales par jour' },
      { value: '100%', label: 'automatisé' },
    ],
    features: [
      'Analyse fondamentale des chiffres économiques',
      'Tous les matins à 8h00',
      'Rapports personnalisés',
      '',
    ],
    bgImage:
      'https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80&w=1920&h=1080',
    color: '#C084FC',
  },
  {
    icon: Users,
    title: 'Communauté VIP',
    description:
      "Échangez avec des traders expérimentés et profitez d'un accompagnement personnalisé.",
    image: 'https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&q=80',
    stats: [
      { value: '8000+', label: 'followers' },
      { value: '7/7', label: 'support' },
      { value: 'VIP', label: 'coaching' },
    ],
    features: [
      'Giveaways',
      'Sessions live hebdomadaires',
      'Groupe privé Discord',
      'Événements exclusifs',
    ],
    bgImage:
      'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&q=80&w=1920&h=1080',
    color: '#D8B4FE',
  },
];

export default function Services({ onOpenRegister }: ServicesProps) {
  // Index de la carte active (celle la plus visible)
  const [activeSection, setActiveSection] = useState(0);
  // Progression de la barre (0 → 1), liée à la carte active
  const [scrollProgress, setScrollProgress] = useState(0);
  // Cartes déjà "visibles" (pour le fade-in)
  const [visibleCards, setVisibleCards] = useState<number[]>([]);

  // Références
  const sectionRef = useRef<HTMLElement>(null);      // section globale
  const progressBarRef = useRef<HTMLDivElement>(null); 
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]); // refs pour chaque card

  // Pour la gestion responsive
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // 1) IntersectionObserver pour les effets de fade-in
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = cardsRef.current.findIndex((ref) => ref === entry.target);
          if (index !== -1 && entry.isIntersecting) {
            setVisibleCards((prev) => [...new Set([...prev, index])].sort());
          }
        });
      },
      {
        rootMargin: '0px 0px 200px 0px',
        threshold: 0.1,
      }
    );

    cardsRef.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  // 2) Sur le scroll, on calcule la carte la plus "centrée" et on met à jour la barre
  const handleScroll = () => {
    const viewportCenter = window.innerHeight / 2;
    let closestCardIndex = 0;
    let minDistance = Infinity;

    // On parcourt toutes les cartes pour trouver celle la plus proche du centre écran
    cardsRef.current.forEach((card, i) => {
      if (!card) return;
      const rect = card.getBoundingClientRect();
      const cardCenter = rect.top + rect.height / 2;
      const distance = Math.abs(cardCenter - viewportCenter);
      if (distance < minDistance) {
        minDistance = distance;
        closestCardIndex = i;
      }
    });

    // Mise à jour de la section active
    setActiveSection(closestCardIndex);

    // Calcul de la progression step-by-step
    // Par exemple, 4 cartes => index 0..3 => 0%, 33%, 66%, 100%
    const maxIndex = services.length - 1;
    const stepProgress = closestCardIndex / maxIndex;
    setScrollProgress(stepProgress);
  };

  // 3) On écoute le scroll + resize pour recalculer la carte active
  useEffect(() => {
    const onScroll = () => requestAnimationFrame(handleScroll);
    const onResize = () => setIsMobile(window.innerWidth < 768);

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });

    // Calcul initial
    handleScroll();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // Fonction pour scroller vers la carte ciblée
  const scrollToSection = (index: number) => {
    const card = cardsRef.current[index];
    if (!card) return;

    const offset = window.innerHeight * 0.3;
    const cardPosition = card.getBoundingClientRect().top + window.pageYOffset;

    window.scrollTo({
      top: cardPosition - offset,
      behavior: 'smooth',
    });
  };

  return (
    <section
      ref={sectionRef}
      id="services"
      className="
        py-24 
        bg-black 
        relative 
        overflow-visible
        bg-left-bottom
        bg-no-repeat
        bg-contain
      "
      style={{
        '--color-1': '#9333EA',
        '--color-2': '#A855F7',
        '--color-3': '#C084FC',
        '--color-4': '#D8B4FE',
      } as React.CSSProperties}
    >

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-16">
          <span className="text-purple-600 font-semibold text-lg mb-4 block">
            NOS SERVICES
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Des outils performants
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-purple-600 mt-2">
              pour votre réussite
            </span>
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Découvrez nos solutions complètes pour améliorer votre trading
          </p>
        </div>

        {/* Barre de progression mobile (step-based) */}
        <div className="md:hidden sticky top-[80px] bg-black/80 backdrop-blur-sm z-30 -mx-4 px-4 py-4">
          <div className="relative h-1 bg-gray-800 rounded-full overflow-hidden">
            {/* On utilise l'activeSection pour avancer par paliers (4 services => 25% par carte) */}
            <div
              className="absolute top-0 left-0 h-full bg-gradient-to-r from-purple-600 to-purple-800 transition-all duration-300"
              style={{ width: `${((activeSection + 1) / services.length) * 100}%` }}
            />
          </div>
          <div className="flex justify-between mt-2">
            {services.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollToSection(index)}
                className={`w-8 h-8 flex items-center justify-center rounded-full transition-all duration-300 ${
                  index <= activeSection ? 'text-purple-400' : 'text-gray-600'
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </div>

        {/* Layout principal : sidebar + cards */}
        <div className="relative flex">
          {/* Sidebar Desktop */}
          <div className="hidden md:block sticky top-32 h-[calc(100vh-8rem)] mr-12">
            <div className="relative h-full flex flex-col items-center justify-between">
              {/* La ligne verticale */}
              <div
                className="absolute left-1/2 top-16 bottom-16 w-0.5 bg-gray-800 transform -translate-x-1/2"
                style={{ zIndex: 0 }}
              >
                {/* Barre colorée en fonction de scrollProgress (0→1) */}
                <div
                  ref={progressBarRef}
                  className="absolute top-0 w-0.5 transition-all duration-300"
                  style={{
                    height: `${scrollProgress * 100}%`,
                    background: `linear-gradient(to bottom, ${services
                      .map((_, i) => `var(--color-${i + 1})`)
                      .join(', ')})`,
                    zIndex: 0,
                  }}
                />
              </div>

              {/* Les étapes */}
              {services.map((service, index) => (
                <div
                  key={index}
                  className="relative z-10 bg-black my-4 px-2 py-2 rounded"
                  style={{ width: '180px' }}
                >
                  <button
                    onClick={() => scrollToSection(index)}
                    className={`relative flex items-center transition-all duration-500 group ${
                      index <= activeSection ? 'opacity-100' : 'opacity-50'
                    }`}
                    style={{ zIndex: 20 }}
                  >
                    <div
                      className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-500`}
                      style={{
                        backgroundColor:
                          index <= activeSection ? service.color : 'rgb(31, 41, 55)',
                        transform: index <= activeSection ? 'scale(1.1)' : 'scale(1)',
                        boxShadow:
                          index <= activeSection
                            ? `0 10px 15px -3px ${service.color}40`
                            : 'none',
                      }}
                    >
                      <service.icon
                        className={`w-5 h-5 transition-colors duration-500 ${
                          index <= activeSection ? 'text-white' : 'text-gray-400'
                        }`}
                      />
                    </div>
                    <div
                      className={`ml-4 transition-all duration-500 ${
                        index <= activeSection ? 'text-white' : 'text-gray-400'
                      }`}
                    >
                      <p className="font-medium">{service.title}</p>
                    </div>
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Service Cards */}
          <div className="flex-1 space-y-32">
            {services.map((service, index) => (
              <div
                key={index}
                ref={(el) => (cardsRef.current[index] = el)}
                className={`transform transition-all duration-1000 ${
                  visibleCards.includes(index)
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-20'
                }`}
              >
                <div className="relative p-8 rounded-3xl overflow-hidden">
                  <div
                    className="absolute inset-0 opacity-5 rounded-3xl"
                    style={{ backgroundColor: service.color }}
                  />
                  <div className="relative z-10">
                    <div className="flex items-center mb-6">
                      <div
                        className="shrink-0 w-12 h-12 rounded-full flex items-center justify-center mr-4"
                        style={{ backgroundColor: `${service.color}33` }}
                      >
                        <service.icon className="w-6 h-6" style={{ color: service.color }} />
                      </div>
                      <h3 className="text-2xl font-bold text-white">{service.title}</h3>
                    </div>
                    <p className="text-gray-400 mb-8">{service.description}</p>
                    <div className="grid grid-cols-3 gap-4 mb-8">
                      {service.stats.map((stat, statIndex) => (
                        <div key={statIndex} className="text-center">
                          <div
                            className="text-2xl font-bold"
                            style={{ color: service.color }}
                          >
                            {stat.value}
                          </div>
                          <div className="text-sm text-gray-400">{stat.label}</div>
                        </div>
                      ))}
                    </div>
                    <div className="space-y-3">
                      {service.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center text-gray-300">
                          <div
                            className="w-1.5 h-1.5 rounded-full mr-3"
                            style={{ backgroundColor: service.color }}
                          />
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Button */}
        <div className="mt-20 text-center">
          <button onClick={onOpenRegister} className="group relative inline-flex items-center">
            <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-purple-800 rounded-full blur opacity-75 group-hover:opacity-100 transition duration-500" />
            <div className="relative px-8 py-4 bg-black rounded-full flex items-center space-x-2 transform hover:scale-105 transition-all duration-500">
              <span className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-purple-600">
                Je m'inscris pour rejoindre le groupe gratuitement
              </span>
              <ArrowRight className="w-5 h-5 text-purple-400 transform group-hover:translate-x-1 transition-transform duration-300" />
            </div>
          </button>
        </div>
      </div>
    </section>
  );
}
